import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from 'src/common/context'
import { alertNotification } from 'src/ui'
import {
  claimPartnerRevenue,
  commentPartnerRevenue,
  EarningPortalData,
  EarningStatement,
  getOrder,
  getPartnerRevenue,
  getPartnerRevenueDocumentUrl,
  getRevenuePreview,
  OrderDetails,
  RevSharingWallet
} from 'src/common/api'
import { getOrganizationProfile } from 'src/common/api/organizations'
import { getOrgReportsSnapshot, SnapshotAsset, SnapshotModel } from 'src/common/api/reportsSnapshot'
import { ChainName } from 'src/common/static'
import { logEvent } from 'src/common/analytics/analytics'
import { buildPortalData, DEFAULT_RATE, mapPartnerRevenueToStatements } from './portalData'
import MetricsHeader from './components/MetricsHeader/MetricsHeader'
import EligibleWalletsPanel from './components/EligibleWalletsPanel/EligibleWalletsPanel'
import StatementsTable from './components/StatementsTable/StatementsTable'
import ClaimModal from './components/ClaimModal/ClaimModal'
import RevenueChart from './components/RevenueChart/RevenueChart'

const isoDate = (date: Date) => date.toISOString()

// QCAD/QCAD_TEST environment heuristic (mirrors the rest of the app).
const defaultAsset = (): SnapshotAsset => {
  const url = process.env.REACT_APP_API_URL || ''
  return url.includes('staging') || url.includes('localhost') || url.includes('sandbox')
    ? SnapshotAsset.QCAD_TEST
    : SnapshotAsset.QCAD
}

// Rendered when there is nothing to show yet (e.g. the org has no wallets, or loading failed). Keeps
// the full portal structure on screen with each section in its empty state, rather than an error screen.
const EMPTY_PORTAL_DATA: EarningPortalData = {
  metrics: {
    currentPeriodLabel: '',
    periodDays: 0,
    netRevenuePayoutMtdQcad: 0,
    effectiveRate: 0,
    avgDailyBalanceMtdQcad: 0,
    unclaimed: null
  },
  eligibleWallets: [],
  statements: [],
  auditLog: []
}

const Earnings: React.FC = () => {
  const { user } = useAuth()
  const organizationId = user?.organization_id || ''

  const [data, setData] = useState<EarningPortalData | null>(null)
  const [loading, setLoading] = useState(true)

  const [claimStatement, setClaimStatement] = useState<EarningStatement | null>(null)
  const [claimOpen, setClaimOpen] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const [isSavingComment, setIsSavingComment] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [snapshots, setSnapshots] = useState<SnapshotModel[]>([])
  // Current-month window + asset, computed once.
  const [asset] = useState<SnapshotAsset>(defaultAsset)
  const [fromDate] = useState(() => {
    const now = new Date()
    return isoDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0)))
  })
  const [toDate] = useState(() => {
    const now = new Date()
    return isoDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59)))
  })

  // A claimed statement's mint order (the /orders endpoint scopes to the caller's org server-side).
  const fetchOrderDetails = async (orderId: string) => {
    if (!orderId) return
    try {
      const response = await getOrder(orderId)
      const freshOrder = response?.orders?.[0]
      if (freshOrder && freshOrder.id) {
        return freshOrder
      }
    } catch (error) {
      console.error('Unable to refresh order details', error)
    }
  }

  // Loads the partner's eligible treasury wallets and their daily balance snapshots for the current
  // month, then aggregates them into the settlement view-model. Statements / audit log have no backend
  // yet, so buildPortalData returns them empty (their sections render their own empty states).
  const fetchData = async () => {
    try {
      const profile = await getOrganizationProfile()
      const allWallets = profile.blockchainDetails || []

      let snapshots: SnapshotModel[] = []
      if (organizationId) {
        try {
          snapshots = await getOrgReportsSnapshot({ organizationId, asset, fromDate, toDate })
        } catch {
          alertNotification('Failed to load wallet balance snapshots. Please try again.', 'error')
        }
      }

      // Defend against the backend's known fromDate filter bug: keep only snapshots whose
      // consolidationDate actually falls within the requested window.
      const scoped = snapshots.filter((s) => {
        const d = s.consolidationDate.slice(0, 10)
        return d >= fromDate.slice(0, 10) && d <= toDate.slice(0, 10)
      })

      setSnapshots(scoped)

      const portal = buildPortalData({
        wallets: allWallets,
        snapshots: scoped,
        effectiveRate: DEFAULT_RATE,
        fromDate,
        toDate
      })

      // Statements are the partner-visible month-end closes (server already filters to this org + visible).
      // The rev-sharing wallet (settlement destination) is the org's isRevenue wallet.
      try {
        const rev = await getPartnerRevenue()
        // Each claimed statement points at its mint order — fetch them so the table can show mint progress.
        const revOrderMap = new Map<string, OrderDetails>()
        for (const record of rev || []) {
          if (record.orderId) {
            const freshOrder = await fetchOrderDetails(record.orderId)
            if (freshOrder) revOrderMap.set(record.id, { ...freshOrder })
          }
        }
        const revWallet = allWallets.find((w) => w.isRevenue)
        const revSharingWallet: RevSharingWallet = revWallet
          ? { name: revWallet.name, address: revWallet.address, chain: revWallet.chain }
          : { name: '—', address: '', chain: ChainName.ETHEREUM }
        portal.statements = mapPartnerRevenueToStatements(rev, revSharingWallet, revOrderMap)
      } catch {
        // Leave statements empty (their section renders its own empty state).
      }

      // Headline metrics (Gross QCAD Earned / Net Rate / Avg Daily Balance) come straight from the backend.
      if (organizationId) {
        try {
          const preview = await getRevenuePreview({ organizationId, fromDate, toDate, asset })
          portal.metrics = {
            ...portal.metrics,
            netRevenuePayoutMtdQcad: Number(preview.grossEarned) || 0,
            effectiveRate: (Number(preview.avgRate) || 0) / 100,
            avgDailyBalanceMtdQcad: Number(preview.avgBalance) || 0
          }
        } catch {
          // Keep the locally-derived metrics on failure.
        }
      }

      setData(portal)
    } catch (err) {
      // On first load (nothing on screen yet) show the empty structure instead of an error screen.
      // If we already had data, a refetch failed - keep it and surface the error.
      if (data) {
        alertNotification('Failed to refresh settlement data. Please try again.', 'error')
      } else {
        setData(EMPTY_PORTAL_DATA)
        alertNotification('Failed to load settlement data. Please try again.', 'error')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const start = performance.now()
    return () => {
      const duration = (performance.now() - start) / 1000
      logEvent('time_spent', { page: 'SettlementsPage', duration_s: duration })
    }
  }, [])

  const openClaim = (statement: EarningStatement) => {
    setClaimStatement(statement)
    setClaimOpen(true)
  }

  const openClaimById = (statementId: string) => {
    const statement = data?.statements.find((s) => s.id === statementId)
    if (statement) openClaim(statement)
  }

  const handleSubmitClaim = async (statement: EarningStatement) => {
    setIsClaiming(true)
    try {
      await claimPartnerRevenue({ organizationId, id: statement.id })
      await fetchData()
      alertNotification('Revenue share claim submitted - mint order queued.', 'success')
      setClaimOpen(false)
    } catch (err) {
      alertNotification('Failed to submit revenue share claim. Please try again.', 'error')
    } finally {
      setIsClaiming(false)
    }
  }

  const handleSaveComment = async (statement: EarningStatement, comments: string) => {
    setIsSavingComment(true)
    try {
      await commentPartnerRevenue({ id: statement.id, comments })
      await fetchData()
      // Keep the open modal in sync with the saved comment.
      setClaimStatement((prev) => (prev && prev.id === statement.id ? { ...prev, comments } : prev))
      alertNotification('Comment saved.', 'success')
    } catch (err) {
      alertNotification('Failed to save comment. Please try again.', 'error')
    } finally {
      setIsSavingComment(false)
    }
  }

  const handleDownloadPdf = async (statementId: string) => {
    setDownloadingId(statementId)
    try {
      const { url } = await getPartnerRevenueDocumentUrl(statementId)
      if (url) {
        window.open(url, '_blank')
      } else {
        alertNotification('No statement is available for this record.', 'error')
      }
    } catch (err) {
      alertNotification('Failed to download statement. Please try again.', 'error')
    } finally {
      setDownloadingId(null)
    }
  }

  const outerSx = { width: '100%', mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }

  // Loading indicator only while there is no data to show.
  if (loading && !data) {
    return (
      <Box sx={outerSx} display="flex" justifyContent="center" py={10}>
        <CircularProgress disableShrink />
      </Box>
    )
  }

  if (!data) return null

  return (
    <Box sx={outerSx}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Service Fee Earnings
        </Typography>
        {data.metrics.currentPeriodLabel && (
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 0.5 }}>
            For the Period {data.metrics.currentPeriodLabel}
          </Typography>
        )}
      </Box>

      <MetricsHeader metrics={data.metrics} onClaim={openClaimById} />

      <RevenueChart snapshots={snapshots} fromDate={fromDate} toDate={toDate} />

      <EligibleWalletsPanel wallets={data.eligibleWallets} />

      <StatementsTable
        statements={data.statements}
        onClaim={openClaim}
        onDownloadPdf={handleDownloadPdf}
        downloadingId={downloadingId}
      />

      {/* <AuditLog entries={data.auditLog} /> */}

      <ClaimModal
        open={claimOpen}
        statement={claimStatement}
        isClaiming={isClaiming}
        isSavingComment={isSavingComment}
        onClose={() => setClaimOpen(false)}
        onSubmit={handleSubmitClaim}
        onSaveComment={handleSaveComment}
      />
    </Box>
  )
}

export default Earnings
